/**
 * WeddingCard: one wedding's summary on the Dashboard (PRD §14.2) — couple
 * names, date, room, guest count, status at a glance, per the original
 * design goal: "scan this in two seconds, not click into each one."
 *
 * The kebab menu (rename/duplicate/archive/delete) renders as a sibling
 * overlay to the card's own <Link>, not nested inside it — see
 * WeddingCardMenu.tsx's header comment for why that's a deliberate
 * structural choice, not an accident.
 */

import { Link } from 'react-router-dom';
import type { Wedding } from '../../domain/types';
import { WeddingCardMenu, type WeddingCardAction } from './WeddingCardMenu';

const ROOM_LABELS: Record<Wedding['room'], string> = {
  candlelight: 'Candlelight',
  conservatory: 'Conservatory',
  tree_deck: 'Tree Deck',
};

const STATUS_STYLES: Record<Wedding['status'], string> = {
  draft: 'bg-sage-100 text-sage-400',
  approved: 'bg-gold-100 text-gold-700',
  finalized: 'bg-evergreen-100 text-evergreen-700',
  archived: 'bg-ivory-200 text-ink-400',
};

const STATUS_LABELS: Record<Wedding['status'], string> = {
  draft: '\u25CF Draft',
  approved: '\u2713 Approved',
  finalized: '\u2713 Finalized',
  archived: '\u{1F4E6} Archived',
};

interface WeddingCardProps {
  key?: React.Key;
  wedding: Wedding;
  canDelete: boolean;
  onAction: (wedding: Wedding, action: WeddingCardAction) => void;
}

export function WeddingCard({ wedding, canDelete, onAction }: WeddingCardProps) {
  const formattedDate = new Date(wedding.eventDate + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const daysUntil = Math.round(
    (new Date(wedding.eventDate + 'T00:00:00').getTime() - new Date().setHours(0, 0, 0, 0)) /
      (1000 * 60 * 60 * 24)
  );

  // Only worth surfacing as urgency when it's genuinely close — beyond
  // a couple of weeks out, "in 47 days" isn't more useful to a
  // coordinator than the calendar date itself already shown below it.
  let urgencyLabel: string | null = null;
  if (daysUntil === 0) urgencyLabel = 'Today';
  else if (daysUntil === 1) urgencyLabel = 'Tomorrow';
  else if (daysUntil > 1 && daysUntil <= 14) urgencyLabel = `In ${daysUntil} days`;

  return (
    <div className="relative rounded-lg border border-ivory-300 bg-white p-4 shadow-card transition-shadow hover:shadow-elevated">
      <div className="absolute right-3 top-3">
        <WeddingCardMenu
          isArchived={wedding.status === 'archived'}
          canDelete={canDelete}
          onAction={(action) => onAction(wedding, action)}
        />
      </div>

      <Link to={`/weddings/${wedding.id}`} className="block">
        <div className="flex items-start justify-between gap-2 pr-7">
          <h3 className="font-display text-lg text-ink-700">{wedding.coupleNames}</h3>
          {urgencyLabel && (
            <span className="shrink-0 rounded-full bg-blush-200 px-2 py-0.5 font-body text-xs font-medium text-ink-600">
              {urgencyLabel}
            </span>
          )}
        </div>
        <p className="mt-0.5 font-body text-sm text-ink-400">{formattedDate}</p>
        <p className="mt-2 font-body text-sm text-ink-500">{ROOM_LABELS[wedding.room]}</p>
        <p className="font-body text-sm text-ink-500">{wedding.guestCount} guests</p>
        <span
          className={`mt-3 inline-block rounded-full px-2.5 py-1 font-body text-xs ${STATUS_STYLES[wedding.status]}`}
        >
          {STATUS_LABELS[wedding.status]}
        </span>
      </Link>
    </div>
  );
}
